const HIDDEN_RE = /^\s*(disregard|duplicate|delete)\b/i;
const HI_NAME_RE = /^\s*(hi|health)\b/i;
/** The Salesforce side label (Rod 2026-10-06: "side can be PLT or DEF or if there is other types"). */
export function sideOf(r) {
    const name = String(r.Name ?? '');
    if (HIDDEN_RE.test(name))
        return 'hidden';
    const rt = String(r.RecordType?.DeveloperName ?? '');
    const rtName = String(r.RecordType?.Name ?? '');
    const party = String(r.Defendant_or_Plaintiff__c ?? '');
    if (rt === 'Health' || HI_NAME_RE.test(name))
        return 'HI';
    if (/worker/i.test(rt) || /worker/i.test(rtName))
        return 'WC';
    if (party === 'Other Party')
        return 'Other Party';
    if (party === 'Defendant')
        return 'DEF';
    if (party === 'Plaintiff' && (rt === 'UM_UIM' || rt === 'Liability'))
        return 'PLT';
    if (/^\s*def\b/i.test(name))
        return 'DEF';
    if (/^\s*plt\b/i.test(name))
        return 'PLT';
    return 'Other';
}
/** A coverage label beside the side pill, when the record's name or Coverage__c says one. */
export function coverageOf(r) {
    const s = `${r.Name ?? ''} ${r.Coverage__c ?? ''}`;
    if (/\b(uber|lyft|rideshare|ride share|doordash|door dash)\b/i.test(s))
        return 'Rideshare';
    if (/\bumbrella\b/i.test(s))
        return 'Umbrella';
    if (/\bmed ?-?pay\b/i.test(s))
        return 'Med-pay';
    if (/\bcommercial\b|\bemployer\b/i.test(s))
        return 'Commercial';
    return null;
}
/** The carrier gate's placeholders (memory call-eligibility-gates): nothing real to call. */
export function isPlaceholderCarrier(name) {
    const n = String(name ?? '').trim().replace(/[\s-]+$/, '').trim();
    if (!n)
        return true;
    return /^(unknown|unk|pending|n\/?a|tbd|tba|delete|none|insurance|def ins(urance)?|plt ins(urance)?)$/i.test(n);
}
/** A carrier name that is not an insurer at all (Kimi's UI assessment change 10, Rod 2026-10-13 "1a 2a"): the record names no one the AI or a
 *  person can call. Wider than isPlaceholderCarrier (which the board already turns into "no carrier"): staff-typed filler that slips past it
 *  ("Unknown Unknown", "unknow unknown", "Unknonw Unknonw", "N/A Insurance"), "no insurance" wordings ("No insurance", "NO VALID INSURANCE
 *  COVER", "Uninsured"), "SELF" / "Self insured", and "Ride" / "Rideshare". The controller's carrier gate already refuses to dial these
 *  (lib/claims_controller/carrier_contact.js kindOfName, the Claims Agent carrier gate); the console only stops showing them as calling work. */
const FILLER = new Set(['pending', 'none', 'tbd', 'tba', 'na', 'n', 'a', 'insurance', 'ins', 'company', 'co', 'def', 'plt', 'other', 'no', 'delete']);
const unknownish = (w) => /^(unk|ukn|unkn|unkw)/.test(w) && w.length <= 9;
const NOT_INSURER = /^(no( valid)? (auto )?insurance( cover(age)?)?|no coverage|uninsured|not insured|none|self|self insured|ride|rideshare|ride share|acord|rental|rental car|rental company)$/;
export function notACarrierName(name) {
    if (isPlaceholderCarrier(name))
        return true;
    const plain = String(name ?? '').toLowerCase().replace(/[^a-z/ ]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!plain)
        return true;
    if (NOT_INSURER.test(plain))
        return true;
    // a record marked for no one to call ("UNKNOWN DO NOT CALL", live 2026-10-20): never an insurer, whatever else the name says
    if (/\bdo not (call|use)\b/.test(plain))
        return true;
    const words = plain.replace(/\//g, '').split(' ').filter(Boolean);
    return words.every((w) => FILLER.has(w) || unknownish(w)) && words.some((w) => unknownish(w) || ['pending', 'none', 'tbd', 'tba', 'na', 'delete'].includes(w));
}
export function dialable(p) {
    const d = String(p ?? '').replace(/\D/g, '');
    return d.length === 10 || (d.length === 11 && d[0] === '1');
}
export const SIDE_ORDER = ['PLT', 'DEF', 'Other Party', 'WC', 'Other'];
/** Rod 10-14 4:22 pm ("hide DO NOT USE placeholders"), done as a label (Cesar 10-15, after four Codex passes found hiding them removed real
 *  work): a record whose own name says DO NOT USE is shown with a "DO NOT USE" tag so a person closes it out in Salesforce. Nothing is
 *  hidden and no rule reads this. The record's own name only (a carrier Account's name is a note shared across cases). */
// the marker anywhere in the record's own name, in any spelling (DONT / DON'T / DON’T / DO NOTUSE / DONOTUSE, full-width or Unicode
// punctuation read as ASCII). The tag quotes the name rather than guessing what it means (Codex 10-15 r7: six passes showed a free-text
// instruction cannot be classified by pattern; "DO NOT USE OLD POLICY NUMBER" is a field note, "DO NOT USE WRONG CLIENT" is the record)
// words apart by spaces, hyphens or underscores ("DO-NOT-USE", "DO_NOT_USE": Codex 10-15 r8); bounded by anything but a letter or digit
const DO_NOT_USE = /(?<![a-z0-9])do[\s_-]*n(?:o[\s_-]*|['’]?)t[\s_-]*use(?![a-z0-9])/i;
const plainName = (s) => s.normalize('NFKC').replace(/[\u2010-\u2015\u2212]/g, '-');
export function markedDoNotUse(r) {
    return DO_NOT_USE.test(plainName(String(r.Name ?? '')));
}
//# sourceMappingURL=side.js.map